import React, { useState } from 'react';
import { Modal, Input, Button, Card, Descriptions, Table, Tag, Space, Alert, Divider } from 'antd';
import { SearchOutlined, UserOutlined, TagOutlined, SwapOutlined, EyeOutlined } from '@ant-design/icons';
import { lookupCustomerMarketer, getCustomerTransactionCategories } from '../../services/referral/marketerService';
import ManualAssignModal from './ManualAssignModal';

const CustomerLookupModal = ({ visible, onClose, onRefresh }) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [resultsList, setResultsList] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [error, setError] = useState(null);

  // Manual Assign modal from Lookup
  const [showAssignModal, setShowAssignModal] = useState(false);

  const handleLookup = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setResultsList([]);
    setSelectedResult(null);
    setCategories([]);
    try {
      const res = await lookupCustomerMarketer(query.trim());
      const resData = res?.data?.data || res?.data || res;
      const rawResults = resData?.results || resData?.content || (Array.isArray(resData) ? resData : []);

      if (Array.isArray(rawResults) && rawResults.length > 0) {
        const formatted = rawResults.map((item, idx) => {
          const cust = item.customer || item;
          const mkt = item.marketer || {};
          return {
            key: cust.customerProfileId || cust.userId || idx,
            customer: cust,
            attribution: item.attribution || (cust.supportCode ? 'MARKETER' : 'NONE'),
            marketer: mkt,
          };
        });
        setResultsList(formatted);
        selectCustomerResult(formatted[0]);
      } else if (resData && (resData.customerUserId || resData.userId || resData.email || resData.supportCode)) {
        // Fallback for single object response format
        const singleObj = {
          key: resData.customerProfileId || resData.userId || 1,
          customer: resData,
          attribution: resData.attribution || (resData.supportCode ? 'MARKETER' : 'NONE'),
          marketer: resData.marketer || {
            name: resData.marketerName,
            email: resData.marketerEmail,
            supportCode: resData.supportCode,
          },
        };
        setResultsList([singleObj]);
        selectCustomerResult(singleObj);
      } else {
        setError('No customer records found matching query.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to lookup customer marketer details.');
    } finally {
      setLoading(false);
    }
  };

  const selectCustomerResult = (resultItem) => {
    setSelectedResult(resultItem);
    const targetUserId = resultItem?.customer?.userId || resultItem?.customer?.customerUserId;
    if (targetUserId) {
      fetchCategories(targetUserId);
    } else {
      setCategories([]);
    }
  };

  const fetchCategories = async (customerUserId) => {
    setCategoriesLoading(true);
    try {
      const res = await getCustomerTransactionCategories(customerUserId);
      const resData = res?.data?.data || res?.data || res;
      const cats = resData?.categories || resData?.content || (Array.isArray(resData) ? resData : []);
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (err) {
      console.error('Error fetching customer categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const resultsColumns = [
    {
      title: 'Customer Name',
      key: 'name',
      render: (_, r) => r.customer?.name || r.customer?.fullName || 'N/A',
    },
    {
      title: 'Email / Phone',
      key: 'contact',
      render: (_, r) => (
        <div>
          <div>{r.customer?.email || 'N/A'}</div>
          <small style={{ color: '#8c8c8c' }}>{r.customer?.phoneNumber || ''}</small>
        </div>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      render: (_, r) => {
        const t = (r.customer?.type || (r.customer?.corporate ? 'CORPORATE' : 'PERSONAL')).toUpperCase();
        return <Tag color={t === 'CORPORATE' ? 'purple' : 'green'}>{t}</Tag>;
      },
    },
    {
      title: 'Attribution',
      key: 'attribution',
      render: (_, r) => {
        const attr = r.attribution || 'NONE';
        const color = attr === 'MARKETER' ? 'green' : attr === 'NONE' ? 'default' : 'orange';
        return <Tag color={color}>{attr}</Tag>;
      },
    },
    {
      title: 'Marketer Onboarded',
      key: 'marketer',
      render: (_, r) => {
        const mktName = r.marketer?.name || r.customer?.marketerName || '—';
        const code = r.marketer?.supportCode || r.customer?.supportCode;
        return (
          <div>
            <div>{mktName}</div>
            {code && <Tag color="volcano" style={{ fontSize: '10px' }}>Code: {code}</Tag>}
          </div>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, r) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          type={selectedResult?.key === r.key ? 'primary' : 'default'}
          onClick={() => selectCustomerResult(r)}
        >
          View
        </Button>
      ),
    },
  ];

  const categoryColumns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (text) => <Tag color="blue">{text || 'GENERAL'}</Tag>,
    },
    {
      title: 'Transaction Count',
      dataIndex: 'count',
      key: 'count',
      render: (val) => val ?? 0,
    },
    {
      title: 'Total Volume',
      dataIndex: 'volume',
      key: 'volume',
      render: (val) => val != null ? `₦${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '₦0.00',
    },
  ];

  const currentCustomer = selectedResult?.customer || {};
  const currentMarketer = selectedResult?.marketer || {};

  return (
    <>
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SearchOutlined style={{ color: '#FF4B01' }} />
            <span>Support Desk: Customer Marketer Lookup</span>
          </div>
        }
        visible={visible}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
        width={850}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Input.Search
            placeholder="Find customer by email, phone, userId, name, ref code or account number..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onSearch={handleLookup}
            enterButton={<Button type="primary" style={{ backgroundColor: '#FF4B01', borderColor: '#FF4B01' }}>Lookup Customer</Button>}
            loading={loading}
            size="large"
          />

          {error && <Alert message={error} type="error" showIcon />}

          {resultsList.length > 0 && (
            <Table
              dataSource={resultsList}
              columns={resultsColumns}
              size="small"
              pagination={false}
              bordered
            />
          )}

          {selectedResult && (
            <Card
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span><UserOutlined /> Customer Attribution Details ({currentCustomer.name || currentCustomer.email})</span>
                  <Button
                    type="primary"
                    icon={<SwapOutlined />}
                    style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                    onClick={() => setShowAssignModal(true)}
                  >
                    Re-attribute Customer
                  </Button>
                </div>
              }
              bordered
            >
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="Customer Name">{currentCustomer.name || currentCustomer.fullName || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Email">{currentCustomer.email || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Phone">{currentCustomer.phoneNumber || currentCustomer.phone || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="User ID">{currentCustomer.userId || currentCustomer.customerUserId || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Attribution Status">
                  <Tag color={selectedResult.attribution === 'MARKETER' ? 'green' : 'orange'}>
                    {selectedResult.attribution || 'NONE'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Support Code">
                  <Tag color="volcano" style={{ fontWeight: 'bold' }}>
                    {currentMarketer.supportCode || currentCustomer.supportCode || 'N/A'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Marketer Name">{currentMarketer.name || currentCustomer.marketerName || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Marketer Email">{currentMarketer.email || currentCustomer.marketerEmail || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Registration Date">{currentCustomer.registeredAt || currentCustomer.createdAt || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Total Transactions">{currentCustomer.transactionCount ?? 0} txs</Descriptions.Item>
              </Descriptions>

              <Divider style={{ margin: '16px 0' }} />

              <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <TagOutlined style={{ color: '#FF4B01' }} />
                Transaction Activity by Category
              </h4>
              <Table
                dataSource={categories}
                columns={categoryColumns}
                loading={categoriesLoading}
                rowKey={(record, idx) => record.category || idx}
                pagination={false}
                size="small"
              />
            </Card>
          )}
        </Space>
      </Modal>

      {showAssignModal && (
        <ManualAssignModal
          visible={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          initialCustomerUserId={currentCustomer.userId || currentCustomer.customerUserId}
          initialSupportCode={currentMarketer.supportCode || currentCustomer.supportCode}
          onSuccess={() => {
            handleLookup();
            if (onRefresh) onRefresh();
          }}
        />
      )}
    </>
  );
};

export default CustomerLookupModal;
